'use client'

import { useEffect, useState } from 'react'

import {
  faFileAlt,
  faFolder,
  faHome,
  faBriefcase,
} from '@document-crunch/pro-regular-svg-icons'

import { DocumentSearchResult } from '@dc-shared-web/hooks/api/document'

import { Icon, IconColor } from '@dc-shared-web/components/base/Icon'
import Spinner from '@dc-shared-web/components/base/Spinner'

import { Button } from '@dc-shared-web/components/shadcn/button'
import { BaseDialog } from '@dc-shared-web/components/base/BaseDialog'

import {
  FolderSelectionTree,
  LocationType,
} from '@/app/(dashboard)/projects/FolderSelectionTree'
import { useMoveDocument } from '@/dc-shared-web/src/hooks/api/document/useMoveDocument'

export function MoveDocumentDialog({
  variant,
  document,
  onSuccess,
  open,
  setOpen,
}: {
  variant: 'crunch'
  document: DocumentSearchResult
  onSuccess: () => void
  open: boolean
  setOpen: (open: boolean) => void
}) {
  const [newLocation, setNewLocation] = useState<LocationType>()
  const { mutate: moveDocument, isLoading: isLoadingMoveDocument } =
    useMoveDocument({
      onSuccess,
    })

  useEffect(() => {
    // Put document in view when folders and projects are loaded
    let elId = ''
    if (document.folderId) {
      elId = `folder-${document.folderId}`
    } else if (document.projectId) {
      elId = `project-${document.projectId}`
    }

    if (elId) {
      const el = window.document.getElementById(elId)
      if (el) {
        el.scrollIntoView({
          block: 'center',
          inline: 'center',
        })
      }
    }
  }, [document])

  const getCurrentLocation = () => {
    if (variant === 'crunch') {
      if (document.projectId) {
        return {
          type: 'project',
          id: document.projectUuid,
          name: document.projectName,
        }
      }
      if (document.folderId) {
        return {
          type: 'folder',
          id: document.folderUuid,
          name: document.folderName,
        }
      }
      return {
        type: 'dashboard',
        name: 'Dashboard',
        id: 'dashboard',
      }
    }
  }

  const currentLocation = getCurrentLocation() as LocationType

  const handleSave = () => {
    if (variant === 'crunch') {
      const folderUuid =
        (newLocation?.id as string) !== 'dashboard'
          ? (newLocation?.id as string)
          : null
      moveDocument({
        crunch_uuid: document.uuid,
        folder_uuid: folderUuid,
        office_uuid: document.teamUUID,
        type: newLocation?.type as 'project' | 'folder' | 'dashboard',
      })
    }
  }

  return (
    <BaseDialog
      title='Move'
      isOpen={open}
      onClose={() => setOpen(false)}
      size='large'
      primaryActionSlot={
        <Button
          disabled={
            !newLocation ||
            newLocation?.id === currentLocation?.id ||
            isLoadingMoveDocument
          }
          onClick={handleSave}
          data-testid='move-document-dialog-move-button'
        >
          {isLoadingMoveDocument ? (
            <div className='tw-flex tw-items-center'>
              <Spinner className='tw-text-white tw-w-5 tw-h-5 tw-mr-2' />
              <span>Moving ...</span>
            </div>
          ) : (
            <>Move</>
          )}
        </Button>
      }
    >
      <div className='tw-text-gray-800 tw-font-normal'>
        <div className='tw-text-2xs'>Document</div>
        <div className='tw-flex tw-items-center tw-mt-1 tw-rounded-md'>
          <Icon
            icon={faFileAlt}
            color={IconColor.secondary}
            className='tw-h-4'
          />
          {/* Swap for readable name? */}
          <div
            className='tw-text-sm tw-ml-2 tw-break-inner'
            data-testid='move-document-dialog-document-name'
          >
            {document.fileName}
          </div>
        </div>

        <div className='tw-text-2xs tw-mt-4'>Current Location</div>
        <div
          data-testid='move-document-dialog-document-team-name'
          className='tw-text-sm tw-mt-1 tw-mb-4 tw-flex tw-items-center tw-break-inner'
        >
          <Icon
            icon={
              currentLocation.type === 'project'
                ? faBriefcase
                : currentLocation.type === 'folder'
                  ? faFolder
                  : faHome
            }
            color={IconColor.secondary}
            className='tw-h-4 tw-mr-2'
          />
          {currentLocation.name}
        </div>

        <FolderSelectionTree
          name={document.fileName || ''}
          type='document'
          teamUuid={document.teamUUID}
          initialLocation={currentLocation}
          selectedLocation={newLocation}
          onLocationChange={setNewLocation}
          itemToMove={document}
        />
      </div>
    </BaseDialog>
  )
}
